import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, inject, Output, ViewChild} from '@angular/core';
import {Team, TeamManagementService} from '../../../../services/api/team-management.service';
import {getFirstLetters} from '../../../../common/utils/helpers';
import {AutoUnsubscribe} from '../../../../decorators/auto-unsubscribe.decorator';
import {SUBSCRIPTIONS} from 'src/app/common/utils/constants';
import {UserPermissionsService} from 'src/app/services/user-permissions.service';
import {AuthService} from 'src/app/auth/shared/services/auth.service';
import {UserInfoQuery} from 'src/app/common/elf-store/user-info/user-info.query';
import {filter, switchMap, take} from 'rxjs';
import {EventEmitterService} from 'src/app/services/event-emitter.service';
import {UserPermissions} from 'src/app/common/elf-store/user-permissions/user-permissions.store';
import {Router} from '@angular/router';
import {TeamQuery} from '../../../../common/elf-store/team/team.query';
import {IUnitDto} from '../../../../common/models/interfaces';
import {NgClass} from '@angular/common';
import {ProgressSpinnerComponent} from '../../../dumb/progress-spinner/progress-spinner.component';
import {MatIconModule} from '@angular/material/icon';

@Component({
  standalone: true,
  selector: 'mc-team-nav-link',
  templateUrl: './team-nav-link.component.html',
  styleUrls: ['./team-nav-link.component.scss'],
  imports: [NgClass, ProgressSpinnerComponent, MatIconModule],
})
@AutoUnsubscribe()
export class TeamNavLinkComponent implements OnInit, OnDestroy {
  teamManagementService = inject(TeamManagementService);
  private userPermissionsService = inject(UserPermissionsService);
  private authService = inject(AuthService);
  private userInfoQuery = inject(UserInfoQuery);
  private eventEmitterService = inject(EventEmitterService);
  private router = inject(Router);
  private teamQuery = inject(TeamQuery);

  @Input() loading?: boolean;
  @Input() isSidenavOpened: boolean;

  @Input() hasMenu = false;
  @Input() menuOpen = false;
  @Output() toggleMenu = new EventEmitter<void>();
  @Output() moveFocusIntoMenu = new EventEmitter<void>();
  @Output() closeMenu = new EventEmitter<void>();

  @ViewChild('menuButton') private menuButton?: ElementRef<HTMLButtonElement>;

  teamShortName: string;
  teamData: IUnitDto;
  showOwnTeam: boolean;

  get isTeamPage() {
    return this.router.url.includes('/team');
  }

  ngOnInit(): void {
    this.authService.isUserSwitchedToTeam
      ? this[SUBSCRIPTIONS].add(
          this.userPermissionsService
            .checkUserPermissions(UserPermissions.manageTeam.teamManagementPage.editTeamService)
            .subscribe(isUserAllowed => (isUserAllowed ? this.loadTeamData() : this.getTeamDataFromMembership())),
        )
      : this.loadTeamData();

    this[SUBSCRIPTIONS].add(
      this.userInfoQuery.userInfo$
        .pipe(
          filter(userInfo => !!userInfo.ssoUserId || !!userInfo.id),
          switchMap(info => {
            const targetId = this.authService.uWTargetSwitchId || info.ssoUserId || info.id;
            return this.userPermissionsService.loadUserPermissions(targetId);
          }),
        )
        .subscribe(),
    );

    this[SUBSCRIPTIONS].add(
      this.teamQuery.teamData$.subscribe(teamData => {
        this.teamData = teamData as IUnitDto;
      }),
    );
    this[SUBSCRIPTIONS].add(
      this.userPermissionsService.checkUserPermissions(UserPermissions.manageTeam.showOwnTeam).subscribe(isShown => {
        this.showOwnTeam = isShown;
      }),
    );
  }

  loadTeamData() {
    this[SUBSCRIPTIONS].add(
      this.teamManagementService.getTeamData().subscribe(() => {
        this.getTeamShortName();
      }),
    );
  }

  getTeamShortName() {
    this.teamShortName = getFirstLetters(this.teamData.name || '').slice(0, 2);
  }

  getTeamDataFromMembership() {
    this.teamManagementService.loadActiveMembership().subscribe(response => {
      const currentTeam = response.find(
        item => item.myMembershipTeamOwnerAccountId === this.authService.uWTargetSwitchId,
      );
      this.teamManagementService.updateTeamData({
        name: currentTeam?.myMembershipTeamName,
        teamMembersCount: null,
        teamMembers: {
          teamImageUrl: currentTeam?.myMembershipTeamOwnerPictureUrl,
        },
      } as Team);
      this.getTeamShortName();
    });
  }

  manageTeam(event: Event) {
    // role="button" on an anchor without href: stop Space from scrolling the page even when navigation is not allowed
    event.preventDefault();
    if (!this.showOwnTeam) return;
    this.goToTeamPage(event);
  }

  onToggleMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggleMenu.emit();
  }

  onMoveFocusIntoMenu(event: Event) {
    event.preventDefault();
    this.moveFocusIntoMenu.emit();
  }

  onCloseMenu(event: Event) {
    event.preventDefault();
    this.closeMenu.emit();
  }

  focusMenuButton() {
    this.menuButton?.nativeElement.focus();
  }

  goToTeamPage(event: Event) {
    event.preventDefault();

    const requiredPermissions = [
      UserPermissions.manageTeam.teamManagementPage.editTeamService,
      UserPermissions.manageTeam.teamManagementPage.enforce2FAService,
      UserPermissions.manageTeam.teamManagementPage.inviteTeamMemberService,
    ];

    this[SUBSCRIPTIONS].add(
      this.userPermissionsService
        .checkUserPermissions(requiredPermissions)
        .pipe(take(1))
        .subscribe(userHavePermission => {
          if (!userHavePermission) {
            this.eventEmitterService.showForbiddenDialog.next(true);
          } else {
            this.router.navigate(['team']);
          }
        }),
    );
  }

  ngOnDestroy() {
    this.teamManagementService.clearTeamData();
  }
}
